import { useEffect, useState } from "react";
import { Play, Square, DollarSign } from "lucide-react";
import { api } from "@/api/client";
import type { StartTimerResult, Suggestion, TimeEntry } from "@/api/types";
import { useWorkspaceId } from "@/app/session";
import { useTimer, invalidateWorkspace } from "@/features/data";
import { ProjectPicker } from "@/components/ProjectPicker";
import { TagPicker } from "@/components/TagPicker";
import { RateClassPicker } from "@/components/RateClassPicker";
import { DescriptionInput } from "@/components/DescriptionInput";
import { Button, ColorDot, IconButton } from "@/components/ui";
import { useToast, errorMessage } from "@/components/Toast";
import { secondsToClock, secondsToHMM, elapsedSeconds } from "@/lib/format";
import { shortcutAllowed } from "@/lib/keys";
import { cn } from "@/lib/cn";

export function TimerBar() {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const { data: running } = useTimer();

  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState<string | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [rateClassId, setRateClassId] = useState<string | null>(null);
  const [billable, setBillable] = useState(false);
  const [busy, setBusy] = useState(false);

  async function start() {
    setBusy(true);
    try {
      const res = await api.post<StartTimerResult>(`/workspaces/${wid}/timer/start`, {
        description,
        project_id: projectId,
        rate_class_id: rateClassId,
        billable,
        tags: tagIds,
      });
      setDescription("");
      setProjectId(null);
      setTagIds([]);
      setRateClassId(null);
      setBillable(false);
      invalidateWorkspace(wid);
      if (res.stopped) {
        toast(`Stopped “${res.stopped.description || "previous entry"}” — ${secondsToHMM(res.stopped.duration_sec)}`, "info");
      }
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function stop() {
    setBusy(true);
    try {
      await api.post<TimeEntry>(`/workspaces/${wid}/timer/stop`);
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  // Global "s" shortcut toggles the timer when not typing or in a dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "s" && shortcutAllowed(e)) {
        e.preventDefault();
        if (running) void stop();
        else void start();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Reset the tab title once nothing is running (RunningBar owns it otherwise).
  useEffect(() => {
    if (!running) document.title = "Timer";
  }, [running]);

  function applySuggestion(s: Suggestion) {
    setDescription(s.description);
    setProjectId(s.project_id);
    setBillable(s.billable);
    setTagIds(s.tags.map((t) => t.id));
  }

  if (running) return <RunningBar entry={running} onStop={stop} busy={busy} />;

  return (
    <div className="flex items-center gap-2 border-b border-line bg-surface px-4 py-2.5">
      <span className="size-2.5 rounded-full border-2 border-faint" aria-hidden />
      <DescriptionInput
        variant="bare"
        value={description}
        onChange={setDescription}
        onPick={applySuggestion}
        onEnter={() => void start()}
        placeholder="What are you working on?"
        autoFocus
      />
      <ProjectPicker
        value={projectId}
        onChange={(id, p) => {
          setProjectId(id);
          if (p) setBillable(p.billable_default);
        }}
        compact
      />
      <TagPicker value={tagIds} onChange={setTagIds} compact />
      <RateClassPicker value={rateClassId} onChange={setRateClassId} compact />
      <IconButton
        label={billable ? "Billable" : "Non-billable"}
        aria-pressed={billable}
        onClick={() => setBillable((b) => !b)}
        className={cn(billable && "text-accent")}
      >
        <DollarSign className="size-4" />
      </IconButton>
      <Button size="sm" onClick={start} loading={busy} className="gap-1.5">
        <Play className="size-3.5 fill-current" /> Start
      </Button>
    </div>
  );
}

function RunningBar({ entry, onStop, busy }: { entry: TimeEntry; onStop: () => void; busy: boolean }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = elapsedSeconds(entry.start_at, now);

  // The tab title is the timer when one is running (DESIGN.md T2).
  useEffect(() => {
    document.title = `▶ ${secondsToClock(elapsed)} · ${entry.description || "Timer"}`;
  }, [elapsed, entry.description]);

  return (
    <div className="flex items-center gap-3 border-b border-line bg-surface px-4 py-2.5">
      <span className="size-2.5 rounded-full bg-running record-live" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ink">{entry.description || <span className="text-faint">No description</span>}</p>
        {entry.project_name && (
          <span className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-muted">
            <ColorDot color={entry.project_color ?? "#888"} />
            {entry.project_name}
          </span>
        )}
      </div>
      <span role="timer" className="mono text-lg font-medium tabular-nums text-running">{secondsToClock(elapsed)}</span>
      <Button size="sm" variant="secondary" onClick={onStop} loading={busy} className="gap-1.5 border-running/40 text-running hover:bg-running/10">
        <Square className="size-3.5 fill-current" /> Stop
      </Button>
    </div>
  );
}
